<script>
import { useAlert } from 'dashboard/composables';
import { mapActions } from 'vuex';
import SettingsSection from '../../../../../../components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import BuhotextCampaignsAPI from 'dashboard/api/buhotextCampaigns';

const I18N_PREFIX = 'INBOX_MGMT.SETTINGS_POPUP.BUHOTEXT';
const POLL_INTERVAL = 5000;

export default {
  components: {
    SettingsSection,
    NextButton,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      I18N_PREFIX,
      deviceName: '',
      isCreating: false,
      deviceData: null,
      serverInfo: null,
      isLoadingServer: false,
      isConnecting: false,
      isDisconnecting: false,
      isLoadingDevice: false,
      isLoadingReconnectables: false,
      reconnectableDevices: [],
      isReconnectingDeviceId: null,
      isDeletingDeviceId: null,
      reconnectCandidate: null,
      reconnectCandidateDetails: null,
      isConfirmingReconnect: false,
      pollTimer: null,
      localDeviceId: null,
      localDeviceName: null,
      localSimId: null,
      localPhoneNumber: null,
    };
  },
  computed: {
    isConfigured() {
      const hasDeviceId = this.inbox.channel?.device_id || this.localDeviceId;
      const hasSimId =
        this.inbox.channel?.sim_subscription_id || this.localSimId;
      return !!(hasDeviceId && hasSimId);
    },
    hasDevice() {
      return !!(this.inbox.channel?.device_id || this.localDeviceId);
    },
    currentDeviceName() {
      return (
        this.localDeviceName || this.inbox.channel?.device_name || 'Unknown'
      );
    },
    currentPhoneNumber() {
      return this.formatPhoneNumber(
        this.localPhoneNumber || this.inbox.channel?.phone_number
      );
    },
    apkDownloadUrl() {
      return this.serverInfo?.apk_download_url || '/downloads/buhotext.apk';
    },
    deviceCode() {
      return this.deviceData?.device_code || '';
    },
    isOnline() {
      if (!this.deviceData?.last_ping_datetime) return false;
      const lastPing = new Date(this.deviceData.last_ping_datetime);
      const now = new Date();
      const diffMinutes = (now - lastPing) / 1000 / 60;
      return diffMinutes < 5;
    },
    statusLabel() {
      return this.isOnline
        ? this.$t(`${I18N_PREFIX}.CONNECTION_STATUS.ONLINE`)
        : this.$t(`${I18N_PREFIX}.CONNECTION_STATUS.OFFLINE`);
    },
    statusClass() {
      return this.isOnline
        ? 'bg-green-50 text-green-500 border border-green-200'
        : 'bg-n-slate-3 text-n-slate-11 border border-n-slate-6';
    },
    simCards() {
      return this.deviceData?.sims || this.deviceData?.sim_subscriptions || [];
    },
    batteryLevel() {
      return this.deviceData?.device_battery || 0;
    },
    batteryIcon() {
      if (this.batteryLevel > 80) return 'i-lucide-battery-full';
      if (this.batteryLevel > 50) return 'i-lucide-battery-medium';
      if (this.batteryLevel > 20) return 'i-lucide-battery-low';
      return 'i-lucide-battery';
    },
    reconnectCandidateCode() {
      if (!this.reconnectCandidate) return '';
      return (
        this.reconnectCandidateDetails?.device_code ||
        this.reconnectCandidate?.device_code ||
        ''
      );
    },
    isReconnectCandidateOnline() {
      return this.isDeviceOnline(this.reconnectCandidateDetails);
    },
  },
  mounted() {
    this.loadServerInfo();
    this.loadReconnectableDevices();
    if (this.hasDevice) {
      this.loadDeviceDetails();
      this.startPolling();
    }
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    ...mapActions('inboxes', ['updateInbox']),

    formatPhoneNumber(phone) {
      const normalized = String(phone || '').trim();
      if (!normalized) return this.$t(`${I18N_PREFIX}.SELECT_SIM.NO_NUMBER`);

      const lowered = normalized.toLowerCase();
      if (lowered === 'null' || lowered === 'undefined' || lowered === '-') {
        return this.$t(`${I18N_PREFIX}.SELECT_SIM.NO_NUMBER`);
      }

      return normalized;
    },

    getSimPhoneNumber(sim) {
      if (!sim) return '';

      return (
        sim.phone_number ||
        sim.phoneNumber ||
        sim.line_number ||
        sim.lineNumber ||
        sim.number ||
        sim.msisdn ||
        sim.display_number ||
        ''
      );
    },

    hasSimPhoneNumber(sim) {
      const value = String(this.getSimPhoneNumber(sim) || '').trim();
      if (!value) return false;

      const lowered = value.toLowerCase();
      return !['null', 'undefined', '-'].includes(lowered);
    },

    isDeviceOnline(device) {
      if (!device?.last_ping_datetime) return false;
      const lastPing = new Date(device.last_ping_datetime);
      const now = new Date();
      const diffMinutes = (now - lastPing) / 1000 / 60;
      return diffMinutes < 5;
    },

    startPolling() {
      this.stopPolling();
      this.pollTimer = setInterval(() => {
        if (this.hasDevice) this.loadDeviceDetails();
      }, POLL_INTERVAL);
    },

    stopPolling() {
      if (!this.pollTimer) return;
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    },

    async loadServerInfo() {
      this.isLoadingServer = true;
      try {
        const response = await BuhotextCampaignsAPI.serverInfo();
        this.serverInfo = response.data;
      } catch (error) {
        // Silent fail - server info is supplementary
      } finally {
        this.isLoadingServer = false;
      }
    },

    async loadDeviceDetails() {
      const devId = this.localDeviceId || this.inbox.channel?.device_id;
      if (!devId) return;
      this.isLoadingDevice = true;
      try {
        const response = await BuhotextCampaignsAPI.getDevice(devId);
        this.deviceData = response.data.data || response.data;
      } catch (error) {
        // Device may not exist anymore
      } finally {
        this.isLoadingDevice = false;
      }
    },

    async loadReconnectableDevices() {
      this.isLoadingReconnectables = true;
      try {
        const response = await BuhotextCampaignsAPI.availableDevices();
        const devices = response.data?.data || [];
        const currentId = String(
          this.inbox.channel?.device_id || this.localDeviceId || ''
        );
        this.reconnectableDevices = devices.filter(
          device => String(device.id) !== currentId
        );

        if (this.reconnectCandidate) {
          const refreshedCandidate = this.reconnectableDevices.find(
            device => String(device.id) === String(this.reconnectCandidate.id)
          );
          if (!refreshedCandidate) {
            this.reconnectCandidate = null;
            this.reconnectCandidateDetails = null;
          }
        }
      } catch {
        this.reconnectableDevices = [];
      } finally {
        this.isLoadingReconnectables = false;
      }
    },

    async handleCreateDevice() {
      if (!this.deviceName.trim()) {
        useAlert(this.$t(`${I18N_PREFIX}.ALERTS.ENTER_NAME`));
        return;
      }

      this.isCreating = true;
      try {
        const response = await this.$store.dispatch(
          'buhotextCampaigns/createDevice',
          { name: this.deviceName.trim() }
        );
        this.deviceData = response.data || response;

        const deviceId = this.deviceData.id;
        this.localDeviceId = deviceId;
        this.localDeviceName = this.deviceName.trim();

        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            device_id: deviceId,
            device_name: this.deviceName.trim(),
          },
        };
        await this.updateInbox(payload);
        await this.loadDeviceDetails();
        this.startPolling();
        this.loadReconnectableDevices();

        useAlert(this.$t(`${I18N_PREFIX}.ALERTS.CREATED`));
      } catch (error) {
        useAlert(this.$t(`${I18N_PREFIX}.ALERTS.CREATE_ERROR`));
      } finally {
        this.isCreating = false;
      }
    },

    async handleSelectSim(sim) {
      this.isConnecting = true;
      try {
        const simId = sim.id || sim.subscription_id;
        const phone = this.getSimPhoneNumber(sim);
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            sim_subscription_id: simId,
            phone_number: phone,
          },
        };
        await this.updateInbox(payload);
        this.localSimId = simId;
        this.localPhoneNumber = phone;
        useAlert(this.$t(`${I18N_PREFIX}.ALERTS.SIM_SELECTED`));
      } catch (error) {
        useAlert(this.$t(`${I18N_PREFIX}.ALERTS.SIM_ERROR`));
      } finally {
        this.isConnecting = false;
      }
    },

    async handleDisconnect() {
      this.isDisconnecting = true;
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            device_id: null,
            sim_subscription_id: null,
            device_name: null,
            phone_number: null,
          },
        };
        await this.updateInbox(payload);
        const disconnectedDevice = this.deviceData
          ? { ...this.deviceData }
          : {
              id: this.localDeviceId || this.inbox.channel?.device_id,
              name: this.localDeviceName || this.inbox.channel?.device_name,
              device_code: this.deviceCode,
            };

        if (disconnectedDevice?.id) {
          const alreadyExists = this.reconnectableDevices.some(
            device => String(device.id) === String(disconnectedDevice.id)
          );
          if (!alreadyExists) {
            this.reconnectableDevices = [
              disconnectedDevice,
              ...this.reconnectableDevices,
            ];
          }
        }

        this.stopPolling();
        this.deviceData = null;
        this.deviceName = '';
        this.reconnectCandidate = null;
        this.reconnectCandidateDetails = null;
        this.localDeviceId = null;
        this.localDeviceName = null;
        this.localSimId = null;
        this.localPhoneNumber = null;
        this.loadReconnectableDevices();
        useAlert(this.$t(`${I18N_PREFIX}.ALERTS.DISCONNECTED`));
      } catch (error) {
        useAlert(this.$t(`${I18N_PREFIX}.ALERTS.DISCONNECT_ERROR`));
      } finally {
        this.isDisconnecting = false;
      }
    },

    async handleReconnectDevice(device) {
      this.isReconnectingDeviceId = device.id;
      try {
        this.reconnectCandidate = device;
        const response = await BuhotextCampaignsAPI.getDevice(device.id);
        this.reconnectCandidateDetails = response.data.data || response.data;
        useAlert(this.$t(`${I18N_PREFIX}.ALERTS.RECONNECT_CODE_READY`));
      } catch {
        useAlert(this.$t(`${I18N_PREFIX}.ALERTS.RECONNECT_ERROR`));
      } finally {
        this.isReconnectingDeviceId = null;
      }
    },

    async confirmReconnectDevice() {
      if (!this.reconnectCandidate) return;

      this.isConfirmingReconnect = true;
      try {
        const response = await BuhotextCampaignsAPI.getDevice(
          this.reconnectCandidate.id
        );
        this.reconnectCandidateDetails = response.data.data || response.data;

        if (!this.isReconnectCandidateOnline) {
          useAlert(this.$t(`${I18N_PREFIX}.ALERTS.RECONNECT_WAITING_ONLINE`));
          return;
        }

        const deviceName =
          this.reconnectCandidate.name ||
          this.reconnectCandidate.device_name ||
          this.$t(`${I18N_PREFIX}.CONNECTION_STATUS.UNKNOWN`);

        await this.updateInbox({
          id: this.inbox.id,
          formData: false,
          channel: {
            device_id: this.reconnectCandidate.id,
            device_name: deviceName,
            sim_subscription_id: null,
            phone_number: null,
          },
        });

        this.localDeviceId = this.reconnectCandidate.id;
        this.localDeviceName = deviceName;
        this.localSimId = null;
        this.localPhoneNumber = null;

        await this.loadDeviceDetails();
        this.startPolling();
        await this.loadReconnectableDevices();

        this.reconnectCandidate = null;
        this.reconnectCandidateDetails = null;
        useAlert(this.$t(`${I18N_PREFIX}.ALERTS.RECONNECTED`));
      } catch {
        useAlert(this.$t(`${I18N_PREFIX}.ALERTS.RECONNECT_ERROR`));
      } finally {
        this.isConfirmingReconnect = false;
      }
    },

    cancelReconnect() {
      this.reconnectCandidate = null;
      this.reconnectCandidateDetails = null;
    },

    async handleDeleteDevice(device) {
      this.isDeletingDeviceId = device.id;
      try {
        await this.$store.dispatch('buhotextCampaigns/deleteDevice', device.id);
        this.reconnectableDevices = this.reconnectableDevices.filter(
          item => String(item.id) !== String(device.id)
        );
        useAlert(this.$t(`${I18N_PREFIX}.ALERTS.DELETED`));
      } catch {
        useAlert(this.$t(`${I18N_PREFIX}.ALERTS.DELETE_ERROR`));
      } finally {
        this.isDeletingDeviceId = null;
      }
    },

    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        useAlert(this.$t(`${I18N_PREFIX}.ALERTS.COPIED`));
      } catch {
        useAlert(this.$t(`${I18N_PREFIX}.ALERTS.COPY_FAILED`));
      }
    },

    handleDownloadApk() {
      window.open(this.apkDownloadUrl, '_blank');
    },

    async refreshDeviceStatus() {
      await this.loadDeviceDetails();
      await this.loadReconnectableDevices();
      useAlert(this.$t(`${I18N_PREFIX}.ALERTS.REFRESHED`));
    },
  },
};
</script>

<template>
  <div>
    <!-- ===== ESTADO 1: Dispositivo completamente configurado ===== -->
    <template v-if="isConfigured">
      <SettingsSection
        :title="$t(`${I18N_PREFIX}.CONNECTION_STATUS.TITLE`)"
        :sub-title="$t(`${I18N_PREFIX}.CONNECTION_STATUS.SUBTITLE`)"
      >
        <div class="space-y-4">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div
                class="w-12 h-12 rounded-full bg-n-purple-3 flex items-center justify-center"
              >
                <i class="i-lucide-smartphone text-xl text-n-purple-11" />
              </div>
              <div>
                <h4 class="font-semibold text-n-slate-12">
                  {{ currentDeviceName }}
                </h4>
                <p class="text-xs text-n-slate-11">
                  {{ currentPhoneNumber }}
                </p>
              </div>
            </div>
            <div class="flex items-center gap-2">
              <span
                :class="statusClass"
                class="px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1"
              >
                <span class="w-2 h-2 rounded-full bg-current" />
                {{ statusLabel }}
              </span>
            </div>
          </div>

          <div v-if="deviceData" class="grid grid-cols-2 gap-3 text-sm">
            <div
              v-if="deviceData.device_brand"
              class="flex items-center justify-between text-n-slate-11 bg-n-slate-2 px-3 py-2 rounded"
            >
              <span>{{ $t(`${I18N_PREFIX}.CONNECTION_STATUS.MODEL`) }}</span>
              <span class="text-n-slate-12 font-medium">
                {{ deviceData.device_brand }} {{ deviceData.device_model }}
              </span>
            </div>
            <div
              v-if="
                deviceData.device_battery !== null &&
                deviceData.device_battery !== undefined
              "
              class="flex items-center justify-between text-n-slate-11 bg-n-slate-2 px-3 py-2 rounded"
            >
              <span>{{ $t(`${I18N_PREFIX}.CONNECTION_STATUS.BATTERY`) }}</span>
              <div class="flex items-center gap-1.5 text-n-slate-12">
                <i :class="batteryIcon" class="text-base" />
                <span class="font-medium">{{ batteryLevel }}%</span>
              </div>
            </div>
          </div>

          <div v-if="simCards.length > 0" class="space-y-1">
            <div class="text-n-slate-11 text-xs font-medium mb-1">
              {{ $t(`${I18N_PREFIX}.CONNECTION_STATUS.SIM_CARDS`) }}
            </div>
            <div
              v-for="sim in simCards"
              :key="sim.id || sim.subscription_id"
              class="flex items-center justify-between text-xs bg-n-slate-2 px-3 py-2 rounded"
            >
              <div class="flex items-center gap-2">
                <i class="i-lucide-sim-card text-sm text-n-blue-11" />
                <span class="text-n-slate-12 font-medium">
                  {{
                    sim.carrier_name || $t(`${I18N_PREFIX}.SELECT_SIM.SIM_CARD`)
                  }}
                </span>
              </div>
              <span v-if="hasSimPhoneNumber(sim)" class="text-n-slate-11">
                {{ formatPhoneNumber(getSimPhoneNumber(sim)) }}
              </span>
            </div>
          </div>

          <div
            v-if="deviceData"
            class="grid grid-cols-2 gap-2 text-xs border-t border-n-weak pt-3"
          >
            <div class="text-center">
              <div class="text-n-slate-11">
                {{ $t(`${I18N_PREFIX}.CONNECTION_STATUS.SMS_SENT`) }}
              </div>
              <div class="text-n-slate-12 font-semibold text-lg">
                {{ deviceData.total_sent_sms || 0 }}
              </div>
            </div>
            <div class="text-center">
              <div class="text-n-slate-11">
                {{ $t(`${I18N_PREFIX}.CONNECTION_STATUS.SMS_PENDING`) }}
              </div>
              <div class="text-n-slate-12 font-semibold text-lg">
                {{ deviceData.total_pending_sms || 0 }}
              </div>
            </div>
          </div>

          <div class="flex items-center gap-2 border-t border-n-weak pt-3">
            <NextButton
              :label="$t(`${I18N_PREFIX}.CONNECTION_STATUS.REFRESH_STATUS`)"
              icon="i-lucide-refresh-cw"
              faded
              sm
              @click="refreshDeviceStatus"
            />
            <NextButton
              :label="$t(`${I18N_PREFIX}.CONNECTION_STATUS.DISCONNECT_DEVICE`)"
              :is-loading="isDisconnecting"
              ruby
              faded
              sm
              @click="handleDisconnect"
            />
          </div>
        </div>
      </SettingsSection>

      <SettingsSection
        :title="$t(`${I18N_PREFIX}.APP_CONFIG.TITLE`)"
        :sub-title="$t(`${I18N_PREFIX}.APP_CONFIG.SUBTITLE`)"
        :show-border="false"
      >
        <div class="space-y-3">
          <div class="space-y-1">
            <label class="text-xs text-n-slate-11 font-medium">
              {{ $t(`${I18N_PREFIX}.APP_CONFIG.DEVICE_CODE`) }}
            </label>
            <div class="flex gap-1 items-stretch">
              <div
                class="flex-1 px-3 py-2 border border-n-weak rounded bg-n-slate-2 text-n-slate-12 font-mono text-lg font-bold tracking-wide"
              >
                {{ deviceCode }}
              </div>
              <button
                class="px-2 py-1.5 rounded border border-n-weak bg-n-slate-3 hover:bg-n-slate-4 text-n-slate-11 hover:text-n-slate-12 transition-colors"
                @click="copyToClipboard(deviceCode)"
              >
                <i class="i-lucide-copy text-xs" />
              </button>
            </div>
          </div>
        </div>
      </SettingsSection>
    </template>

    <!-- ===== ESTADO 2: Dispositivo creado pero sin SIM seleccionada ===== -->
    <template v-else-if="hasDevice">
      <SettingsSection
        :title="$t(`${I18N_PREFIX}.SELECT_SIM.TITLE`)"
        :sub-title="$t(`${I18N_PREFIX}.SELECT_SIM.SUBTITLE`)"
      >
        <div class="space-y-4">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-12 h-12 rounded-full bg-n-purple-3 flex items-center justify-center"
            >
              <i class="i-lucide-smartphone text-xl text-n-purple-11" />
            </div>
            <div>
              <h4 class="font-semibold text-n-slate-12">
                {{ currentDeviceName }}
              </h4>
              <p v-if="deviceCode" class="text-xs text-n-slate-11">
                {{ $t(`${I18N_PREFIX}.APP_CONFIG.DEVICE_CODE`) }}:
                {{ deviceCode }}
              </p>
            </div>
          </div>

          <div
            v-if="isLoadingDevice"
            class="flex items-center gap-2 text-sm text-n-slate-11"
          >
            <div
              class="animate-spin rounded-full h-4 w-4 border-b-2 border-n-brand"
            />
            {{ $t(`${I18N_PREFIX}.SELECT_SIM.LOADING`) }}
          </div>

          <div v-else-if="simCards.length > 0" class="space-y-2">
            <div class="text-sm font-medium text-n-slate-12 mb-2">
              {{ $t(`${I18N_PREFIX}.SELECT_SIM.AVAILABLE`) }}
            </div>
            <button
              v-for="sim in simCards"
              :key="sim.id || sim.subscription_id"
              class="w-full flex items-center justify-between p-3 rounded-lg border border-n-weak hover:border-n-blue-9 hover:bg-n-blue-2 transition-all text-left"
              :disabled="isConnecting"
              @click="handleSelectSim(sim)"
            >
              <div class="flex items-center gap-3">
                <i class="i-lucide-sim-card text-lg text-n-blue-11" />
                <div>
                  <div class="text-sm font-medium text-n-slate-12">
                    {{
                      sim.carrier_name ||
                      $t(`${I18N_PREFIX}.SELECT_SIM.SIM_CARD`)
                    }}
                  </div>
                  <div
                    v-if="hasSimPhoneNumber(sim)"
                    class="text-xs text-n-slate-11"
                  >
                    {{ formatPhoneNumber(getSimPhoneNumber(sim)) }}
                  </div>
                </div>
              </div>
              <i class="i-lucide-chevron-right text-n-slate-11" />
            </button>
          </div>

          <div
            v-else
            class="bg-n-yellow-2 border border-n-yellow-6 rounded-lg p-4 text-sm"
          >
            <div class="flex gap-2">
              <i class="i-lucide-alert-triangle text-n-yellow-11 mt-0.5" />
              <div class="text-n-yellow-11">
                <p class="font-medium mb-1">
                  {{ $t(`${I18N_PREFIX}.SELECT_SIM.NO_SIMS_TITLE`) }}
                </p>
                <p>
                  {{ $t(`${I18N_PREFIX}.SELECT_SIM.NO_SIMS_DESC`) }}
                </p>
              </div>
            </div>
          </div>

          <div v-if="deviceCode" class="space-y-2 pt-3 border-t border-n-weak">
            <div class="text-n-slate-11 text-xs font-medium mb-1">
              {{ $t(`${I18N_PREFIX}.APP_CONFIG.TITLE`) }}
            </div>
            <div class="space-y-1">
              <label class="text-xs text-n-slate-11">
                {{ $t(`${I18N_PREFIX}.APP_CONFIG.DEVICE_CODE`) }}
              </label>
              <div class="flex gap-1 items-stretch">
                <div
                  class="flex-1 px-3 py-2 border border-n-weak rounded bg-n-slate-2 text-n-slate-12 font-mono text-lg font-bold tracking-wide"
                >
                  {{ deviceCode }}
                </div>
                <button
                  class="px-2 py-1 rounded border border-n-weak bg-n-slate-3 hover:bg-n-slate-4 text-n-slate-11 hover:text-n-slate-12 transition-colors"
                  @click="copyToClipboard(deviceCode)"
                >
                  <i class="i-lucide-copy text-xs" />
                </button>
              </div>
            </div>
          </div>

          <div class="flex items-center gap-2 pt-3 border-t border-n-weak">
            <NextButton
              :label="$t(`${I18N_PREFIX}.SELECT_SIM.DOWNLOAD_APK`)"
              icon="i-lucide-download"
              slate
              faded
              sm
              @click="handleDownloadApk"
            />
            <NextButton
              :label="$t(`${I18N_PREFIX}.SELECT_SIM.REFRESH`)"
              icon="i-lucide-refresh-cw"
              faded
              sm
              @click="loadDeviceDetails"
            />
            <NextButton
              :label="$t(`${I18N_PREFIX}.SELECT_SIM.DISCONNECT`)"
              ruby
              faded
              sm
              :is-loading="isDisconnecting"
              @click="handleDisconnect"
            />
          </div>
        </div>
      </SettingsSection>
    </template>

    <!-- ===== ESTADO 3: Sin dispositivo - Crear nuevo ===== -->
    <template v-else>
      <SettingsSection
        :title="$t(`${I18N_PREFIX}.CONNECT_DEVICE.TITLE`)"
        :sub-title="$t(`${I18N_PREFIX}.CONNECT_DEVICE.SUBTITLE`)"
      >
        <div class="space-y-6">
          <div
            v-if="reconnectableDevices.length > 0 || isLoadingReconnectables"
            class="space-y-3"
          >
            <div class="flex items-center justify-between">
              <h4 class="text-sm font-semibold text-n-slate-12">
                {{ $t(`${I18N_PREFIX}.CONNECT_DEVICE.RECONNECTABLE_TITLE`) }}
              </h4>
              <NextButton
                :label="$t(`${I18N_PREFIX}.SELECT_SIM.REFRESH`)"
                icon="i-lucide-refresh-cw"
                faded
                sm
                @click="loadReconnectableDevices"
              />
            </div>

            <div v-if="isLoadingReconnectables" class="text-sm text-n-slate-11">
              {{ $t(`${I18N_PREFIX}.CONNECT_DEVICE.LOADING_RECONNECTABLE`) }}
            </div>

            <div v-else class="space-y-3">
              <div
                v-for="device in reconnectableDevices"
                :key="device.id"
                class="rounded-lg border border-n-weak bg-n-alpha-2 p-4 space-y-3"
              >
                <div>
                  <p class="text-sm font-semibold text-n-slate-12">
                    {{
                      device.name ||
                      device.device_name ||
                      $t(`${I18N_PREFIX}.CONNECTION_STATUS.UNKNOWN`)
                    }}
                  </p>
                  <p class="text-xs text-n-slate-11">
                    {{ $t(`${I18N_PREFIX}.CONNECT_DEVICE.DEVICE_ID`) }}:
                    {{ device.id }}
                  </p>
                </div>

                <div
                  v-if="
                    reconnectCandidate &&
                    String(reconnectCandidate.id) === String(device.id) &&
                    reconnectCandidateCode
                  "
                  class="rounded-md border border-n-blue-6 bg-n-blue-3 px-4 py-3 text-center"
                >
                  <div class="text-xs text-n-slate-11 mb-1">
                    {{ $t(`${I18N_PREFIX}.APP_CONFIG.DEVICE_CODE`) }}
                  </div>
                  <div class="text-xl font-bold tracking-wide text-n-blue-12">
                    {{ reconnectCandidateCode }}
                  </div>
                </div>

                <div
                  v-if="
                    reconnectCandidate &&
                    String(reconnectCandidate.id) === String(device.id)
                  "
                  class="rounded-md border border-n-weak bg-n-alpha-3 p-3 space-y-2"
                >
                  <p class="text-xs text-n-slate-11">
                    {{ $t(`${I18N_PREFIX}.CONNECT_DEVICE.RECONNECT_HELP`) }}
                  </p>
                  <p
                    class="text-xs"
                    :class="
                      isReconnectCandidateOnline
                        ? 'text-green-500'
                        : 'text-n-yellow-11'
                    "
                  >
                    {{
                      isReconnectCandidateOnline
                        ? $t(`${I18N_PREFIX}.CONNECTION_STATUS.ONLINE`)
                        : $t(`${I18N_PREFIX}.CONNECTION_STATUS.OFFLINE`)
                    }}
                  </p>
                  <div class="flex items-center gap-2">
                    <NextButton
                      :label="
                        $t(`${I18N_PREFIX}.CONNECT_DEVICE.CONNECT_NOW_BUTTON`)
                      "
                      icon="i-lucide-check"
                      sm
                      :is-loading="isConfirmingReconnect"
                      @click="confirmReconnectDevice"
                    />
                    <NextButton
                      :label="
                        $t(
                          `${I18N_PREFIX}.CONNECT_DEVICE.CANCEL_RECONNECT_BUTTON`
                        )
                      "
                      faded
                      slate
                      sm
                      @click="cancelReconnect"
                    />
                  </div>
                </div>

                <div class="flex items-center gap-2">
                  <NextButton
                    :label="
                      $t(`${I18N_PREFIX}.CONNECT_DEVICE.RECONNECT_BUTTON`)
                    "
                    icon="i-lucide-link"
                    sm
                    :is-loading="isReconnectingDeviceId === device.id"
                    @click="handleReconnectDevice(device)"
                  />
                  <NextButton
                    :label="$t(`${I18N_PREFIX}.CONNECT_DEVICE.DELETE_BUTTON`)"
                    icon="i-lucide-trash-2"
                    ruby
                    faded
                    sm
                    :is-loading="isDeletingDeviceId === device.id"
                    @click="handleDeleteDevice(device)"
                  />
                </div>
              </div>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ $t(`${I18N_PREFIX}.CONNECT_DEVICE.DEVICE_NAME`) }}
            </label>
            <input
              v-model="deviceName"
              type="text"
              :placeholder="
                $t(`${I18N_PREFIX}.CONNECT_DEVICE.DEVICE_NAME_PLACEHOLDER`)
              "
              class="w-full reset-base py-2 px-3 border border-n-weak rounded-lg text-sm text-n-slate-12 bg-n-solid-2 focus:border-n-brand focus:ring-1 focus:ring-n-brand"
            />
          </div>

          <div class="text-center py-4 border-t border-n-weak">
            <div
              class="w-14 h-14 mx-auto mb-3 rounded-full bg-green-100 flex items-center justify-center"
            >
              <i class="i-lucide-download text-2xl text-green-500" />
            </div>
            <h4 class="font-semibold text-n-slate-12 mb-1">
              {{ $t(`${I18N_PREFIX}.CONNECT_DEVICE.DOWNLOAD_TITLE`) }}
            </h4>
            <p class="text-sm text-n-slate-11 mb-4">
              {{ $t(`${I18N_PREFIX}.CONNECT_DEVICE.DOWNLOAD_DESC`) }}
            </p>
            <NextButton
              :label="$t(`${I18N_PREFIX}.CONNECT_DEVICE.DOWNLOAD_BUTTON`)"
              icon="i-lucide-download"
              slate
              @click="handleDownloadApk"
            />
          </div>

          <div
            class="bg-n-blue-3 border border-n-blue-6 rounded-lg p-4 text-sm"
          >
            <div class="flex gap-2">
              <i class="i-lucide-info text-n-blue-11 mt-0.5 flex-shrink-0" />
              <div class="text-n-blue-11">
                <p class="font-medium mb-1">
                  {{ $t(`${I18N_PREFIX}.CONNECT_DEVICE.NEXT_STEPS_TITLE`) }}
                </p>
                <p>
                  {{ $t(`${I18N_PREFIX}.CONNECT_DEVICE.NEXT_STEPS_DESC`) }}
                </p>
              </div>
            </div>
          </div>

          <div class="flex justify-end">
            <NextButton
              :label="$t(`${I18N_PREFIX}.CONNECT_DEVICE.CREATE_BUTTON`)"
              icon="i-lucide-plus"
              :is-loading="isCreating"
              :disabled="!deviceName.trim()"
              @click="handleCreateDevice"
            />
          </div>
        </div>
      </SettingsSection>
    </template>
  </div>
</template>
